import { inject, Injectable, signal } from '@angular/core'
import { MAP } from '@bases/base.token'
import { HttpService } from '@bases/http.service'
import { API_URL } from '@core/constants/constant.data'
import { LAYERS_ARRAY } from '@core/constants/layers.data'
import { HECTARE, LAU2 } from '@core/constants/scale.data'
import { asyncError, asyncLoading, AsyncState, asyncSuccess } from '@core/models/async-state.model'
import { LayerStandard } from '@core/models/layers.model'
import { parseToFront, Snapshot } from '@core/models/snapshot.model'
import { GeoServerService } from '@core/services/geoserver.service'
import { UploadedLayersService } from '@core/services/layers-uploaded.service'
import { LayersService } from '@core/services/layers.service'
import { MapService } from '@core/services/map.service'
import { SelectionScaleService } from '@core/services/selection-scale.service'
import { SelectionToolService } from '@core/services/selection-tool.service'
import { environment } from '@env/environment'
import * as L from 'leaflet'
import { BehaviorSubject } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class SnapshotService extends HttpService {
  private readonly _map = inject<BehaviorSubject<L.DrawMap>>(MAP)
  private readonly _mapService = inject(MapService)
  private readonly _selectionToolService = inject(SelectionToolService)
  private readonly _selectionScaleService = inject(SelectionScaleService)
  private readonly _uploadedLayersService = inject(UploadedLayersService)
  private readonly _layersService = inject(LayersService)
  private readonly _geoServerService = inject(GeoServerService)

  protected _baseUrl = API_URL + '/snapshots'
  layers$ = this._layersService.filteredLayers$
  snapshotsState$ = signal<AsyncState<Snapshot[]>>(asyncLoading())
  snapshotsSharedWithMeState$ = signal<AsyncState<Snapshot[]>>(asyncLoading())

  constructor() {
    super()
  }

  async getSnapshots() {
    this.snapshotsState$.set(asyncLoading())
    const response = await this.GETlist()

    if (response === null) {
      this.snapshotsState$.set(asyncError())
      return
    }

    const snapshotsFormatted: Snapshot[] = []
    response.forEach((snapshot: Snapshot) => {
      snapshotsFormatted.push(parseToFront(snapshot))
    })

    this.snapshotsState$.set(asyncSuccess(snapshotsFormatted))
  }

  async getSnapshotsSharedWithMe() {
    this.snapshotsSharedWithMeState$.set(asyncLoading())
    const response = await this.GETlist(this._baseUrl + '/shared')

    if (response === null) {
      this.snapshotsSharedWithMeState$.set(asyncError())
      return
    }

    const snapshotsFormatted: Snapshot[] = []
    response.forEach((snapshot: Snapshot) => {
      snapshotsFormatted.push(parseToFront(snapshot))
    })
    this.snapshotsSharedWithMeState$.set(asyncSuccess(snapshotsFormatted))
  }

  async addSnapshot(name: string, description: string = '', sharedWith: string[] = []) {
    const currentScaleLevel = this._selectionScaleService.currentScaleLevel$()
    const scaleLevelDisplayName = currentScaleLevel?.displayName ?? ''
    const selectedLayers = this._layersService.selectedLayers$()
    const selectedUploadedLayerIds = this._uploadedLayersService.selectedUploadLayersIds$()

    const date = new Date()
    const zones =
      scaleLevelDisplayName !== HECTARE
        ? this._selectionToolService.nutsIds$()
        : this._selectionToolService.hectareAreas$().map((area) => {
            if (area instanceof L.Circle) {
              let circle: any = area as L.Circle
              const radius = circle.getRadius()
              circle = circle.toGeoJSON()
              circle.properties.radius = radius
              return circle
            } else {
              const geoJson: any = (area as L.Polygon).toGeoJSON()
              let features = []
              if ('features' in geoJson) {
                features = geoJson.features[0]
              } else {
                features = geoJson
              }
              return features
            }
          })

    const snap: Snapshot = {
      name: name,
      description: description,
      date: date,
      sharedWith: sharedWith,
      scale: scaleLevelDisplayName,
      zones: zones,
      layers: selectedLayers,
      uploadedLayers: selectedUploadedLayerIds,
      center: this._map.getValue().getCenter(),
      zoom: this._map.getValue().getZoom(),
    }

    return await this.POST(snap)
  }

  async updateSnapshot(
    id: number,
    name: string,
    description: string = '',
    sharedWith: string[] = [],
  ) {
    // let form = new FormData()

    // form.append('id', JSON.stringify(id))
    // form.append('name', name)
    // if (description) {
    //   form.append('description', description)
    // }
    // form.append('shared_with', JSON.stringify(sharedWith))

    const snap_edited = {
      id: id,
      name: name,
      description: description,
      shared_with: sharedWith,
    }

    return await this.PUT(id, snap_edited)
  }

  async apply(snapshot: Snapshot) {
    const myMap = this._map.getValue()

    // remove all selections
    this._selectionToolService.clearAll()

    // Match layers of snapshot to the current list
    const layers2Toggle: Array<LayerStandard> = []
    const lay: Array<string> = snapshot.layers
    for (var i = 0; i < lay.length; i++) {
      LAYERS_ARRAY.forEach((layer) => {
        if (layer.workspacename === lay[i]) {
          layers2Toggle.push(layer)
        }
      })
    }
    // Display on map normal layer
    this._layersService.removeAllLayers()
    layers2Toggle.forEach((layer) => {
      this._layersService.showOrRemoveLayerWithBoolean(layer.workspacename, layer.order, true)
    })

    // Display on map uploaded layer
    this._uploadedLayersService.unCheckAllUploadedLayers()
    const notFoundSharedIds = [] // Track IDs that weren't found

    snapshot.uploadedLayers.forEach((id) => {
      let foundInPersonal = false
      let foundInShared = false

      if (this._uploadedLayersService.personalLayers$()) {
        foundInPersonal = this._uploadedLayersService.personalLayers$().some((personalLayer) => {
          if (personalLayer.id == id) {
            this._uploadedLayersService.actionLayer('personal', personalLayer)
            return true
          }
          return false
        })
      }

      if (!foundInPersonal && this._uploadedLayersService.sharedLayers$()) {
        foundInShared = this._uploadedLayersService.sharedLayers$().some((sharedLayer) => {
          if (sharedLayer.id == id) {
            this._uploadedLayersService.actionLayer('shared', sharedLayer)
            return true
          }
          return false
        })
      }

      if (!foundInShared && !foundInPersonal) {
        notFoundSharedIds.push(id)
      }
    })

    if (notFoundSharedIds.length) {
      this._toasterService.openSnackBarError(
        `At least one layer featured in this snapshot was either deleted or was not shared with you.`,
      )
    }

    this._selectionScaleService.setSelectableArea(snapshot.scale)

    // Wait for the next tick to ensure the scale service has finished.
    await new Promise((resolve) => setTimeout(resolve, 0))

    const scaleLevel = this._selectionScaleService.currentScaleLevel$()

    myMap.flyTo(snapshot.center, snapshot.zoom)

    if (scaleLevel) {
      if (scaleLevel.displayName != HECTARE) {
        // Working but a little slow
        const isLau2: boolean = scaleLevel.displayName == LAU2
        let nameId = isLau2 ? 'comm_id' : 'nuts_id'
        const layer = scaleLevel.geoServer

        if (environment.appName == 'openMod4Africa') {
          nameId = 'admin_geom_id'
        }

        const nuts_ids = `${nameId}='${snapshot.zones.join(`' OR ${nameId}='`)}'`
        const url = this._geoServerService.getGeoServerUrl(layer, '', nuts_ids, '')

        this._http.get(url).subscribe({
          next: (result: any) => {
            ;(result.features || []).forEach((geo: any) => this._mapService.selectAreaWithNuts(geo))
          },
          error: (e) => {
            console.error(e)
            // To override 404 message of geoserver
            this._toasterService.openSnackBarError(`Failed get snapshot from geoserver`)
            return false
          },
        })
      } else {
        snapshot.zones.forEach((zone) => {
          let shape: any
          if (zone.properties && zone.properties.radius) {
            shape = L.geoJSON(zone as any, {
              pointToLayer: (feature: any, latlng: L.LatLng) => {
                return new L.Circle(latlng, feature.properties.radius)
              },
            })
            shape.radius = zone.properties.radius
            shape.latLng = L.GeoJSON.coordsToLatLng(zone.geometry.coordinates)
          } else {
            shape = L.polygon(L.GeoJSON.coordsToLatLngs(zone.geometry.coordinates[0]))
          }
          this._selectionToolService.drawHectaresLoadingResult(shape)
        })
      }
    }
    return true
  }

  async deleteSnapshot(id: number) {
    return await this.DELETE(id)
  }

  async deleteSharedSnapshot(id: number) {
    return await this.DELETE(id, this._baseUrl + '/shared/' + id)
  }

  refreshSnapshots() {
    this.getSnapshots()
    this.getSnapshotsSharedWithMe()
  }
}
